<template>
    <div class="animated fadeIn">
        <b-row>
            <b-col cols="12">
                <ComponentHeader
                    title="Coupon Code"
                    :is-loading="loading"
                    :has-search="false"
                    :has-clear="false"
                    @reload-data="getCouponCodeList"
                    @create-press="handleCreatePress"
                    createButtonText="Create Coupon"
                />
            </b-col>
        </b-row>

        <b-card class="card-wrap mt-2" border-variant="info">
            <b-row class="my-2">
                <b-col md="3">
                    <b-form-group>
                        <EbInput
                            id="couponCode"
                            v-model="filterCouponCode"
                            label="Coupon Code"
                            placeholder="Coupon Code"
                            type="text"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group>
                        <label>Time Period</label>
                        <EbSelect
                            id="selecttime"
                            v-model="selectedPeriod"
                            type="select"
                            :options="periodList"
                            label="text"
                            :selected="selectedPeriod"
                            @input="handlePeriodChange"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group>
                        <label>Select Provider</label>
                        <EbSelect
                            id="selectprovider"
                            v-model="selectedProvider"
                            type="select"
                            :options="partnerList"
                            label="partnerName"
                            :selected="selectedProvider"
                            placeholder="Select Provider"
                            @input="handleProviderChange"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group>
                        <label>Status</label>
                        <EbSelect
                            id="selectstatus"
                            v-model="selectedStatus"
                            type="select"
                            :options="statusOptions"
                            :selected="selectedStatus"
                            placeholder="Select Status"
                            @input="getCouponCodeList"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <div class="mt-2">
                <b-table
                    id="coupon-table"
                    :dark="tableOptions.dark"
                    :hover="tableOptions.hover"
                    :striped="tableOptions.striped"
                    :bordered="tableOptions.bordered"
                    :small="tableOptions.small"
                    :fixed="tableOptions.fixed"
                    :filter="filterCouponCode"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :items="couponCodeList"
                    :fields="columns"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    responsive
                    show-empty
                    thead-class="header"
                    class="mt-3"
                    @filtered="handleFilter"
                >
                    <template #cell(action)="data">
                        <ActionButtonGroup
                            showView
                            showEdit
                            showDelete
                            @view="handleView(data.item)"
                            @edit="handleEdit(data.item)"
                            @delete="handleDelete(data)"
                        />
                    </template>

                    <template #empty="scope">
                        <h4>{{ scope.emptyText }}</h4>
                    </template>
                </b-table>
            </div>

            <div
                v-if="shouldShowPagination"
                class="d-flex justify-content-end align-items-center mt-3"
            >
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    size="md"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                    align="right"
                />
            </div>
        </b-card>
    </div>
</template>

<script>
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import EbSelect from "@/components/FormElements/Select";
import EbInput from "@/components/FormElements/Input";
import ComponentHeader from "@/components/componentNewHeader";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";

// Constants
const DATE_FORMAT = "DD/MM/YYYY";
const DEFAULT_ALL_OPTION = { partnerName: "ALL", _id: "" };
const STATUS_OPTIONS = ["ALL", "PENDING APPROVAL", "APPROVED"];
const PERIOD_OPTIONS = [
  { text: "Last 7 days", value: 7 },
  { text: "Last 15 days", value: 15 },
  { text: "Last 30 days", value: 30 },
  { text: "Last 3 Months", value: 90 },
  { text: "Last 6 Months", value: 180 }
];

export default {
  name: "CouponDetails",

  components: {
    EbSelect,
    EbInput,
    ComponentHeader,
    ActionButtonGroup
  },

  data() {
    return {
      // Loading state
      loading: false,

      // Table configuration
      tableOptions: {
        hover: true,
        striped: false,
        bordered: true,
        small: false,
        dark: false,
        fixed: false
      },

      // Pagination
      perPage: 10,
      currentPage: 1,

      // Filters
      filterCouponCode: "",
      selectedStatus: "ALL",
      selectedProvider: { ...DEFAULT_ALL_OPTION },
      selectedPeriod: PERIOD_OPTIONS[0],

      // Sorting
      sortBy: "createdAt",
      sortDesc: true,

      // Options
      periodList: PERIOD_OPTIONS,
      statusOptions: STATUS_OPTIONS,

      // Table columns
      columns: [
        { key: "Coupon Code", label: "Coupon Code", sortable: true },
        { key: "Coupon value", label: "Coupon Value", sortable: false },
        { key: "Start Date", label: "Start Date", sortable: false },
        { key: "End Date", label: "End Date", sortable: false },
        { key: "action", label: "Action", sortable: false }
      ]
    };
  },

  computed: {
    ...mapGetters({
      couponCode: "coupon/couponCodeList"
    }),

    couponCodeList() {
      return this.couponCode.map(data => ({
        _id: data._id,
        "Coupon Code": data.promoCode?.toUpperCase() || "",
        "Coupon value": data.promoRules?.rewardValue || "",
        "Start Date": this.formatDate(data.promoRules?.startDate),
        "End Date": this.formatDate(data.promoRules?.endDate),
        isApproved: data.isApproved
      }));
    },

    partnerList() {
      const data = this.$store.getters["partner/GetPartnerLiteList"];

      if (!data || !data.length) {
        return [DEFAULT_ALL_OPTION];
      }

      const partners = data.map(obj => ({
        ...obj,
        partnerName: obj.organization?.name || ""
      }));

      return [DEFAULT_ALL_OPTION, ...partners];
    },

    totalRows() {
      return this.couponCodeList.length;
    },

    shouldShowPagination() {
      return this.totalRows > this.perPage;
    }
  },

  mounted() {
    this.initializeData();
  },

  methods: {
    ...mapActions({
      fetchPartnerList: "partner/fetchPartnerListLite"
    }),

    // Initialization
    async initializeData() {
      await Promise.all([
        this.getCouponCodeList(),
        this.fetchPartnerList({ type: "SELLER" })
      ]);
    },

    // Data fetching
    async getCouponCodeList() {
      this.loading = true;

      try {
        const payload = this.buildFetchPayload();
        await this.$store.dispatch("coupon/promotionListByFilter", payload);
      } catch (error) {
        console.error("Error fetching coupons:", error);
        this.$toasted.error("Failed to fetch coupon codes");
      } finally {
        this.loading = false;
      }
    },

    buildFetchPayload() {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - this.selectedPeriod.value);

      const payload = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      // Add provider ID based on user role
      if (this.$userRole === "Tenant") {
        if (this.selectedProvider?._id) {
          payload.providerId = this.selectedProvider._id;
        }
      } else {
        payload.providerId = this.$currentUserData?._id;
      }

      // Add status filter
      if (this.selectedStatus !== "ALL") {
        payload.status = this.selectedStatus === "APPROVED";
      }

      return payload;
    },

    // Event handlers
    handleCreatePress() {
      this.$router.push({ name: "CreateCoupon" });
    },

    handleProviderChange() {
      this.getCouponCodeList();
    },

    handlePeriodChange() {
      this.getCouponCodeList();
    },

    handleFilter() {
      this.currentPage = 1;
    },

    handleView(item) {
      const coupon = this.getCouponById(item._id);
      if (coupon) {
        this.$router.push({
          name: "ViewCoupon",
          params: { data: coupon }
        });
      }
    },

    handleEdit(item) {
      const coupon = this.getCouponById(item._id);
      if (coupon) {
        this.$router.push({
          name: "EditCoupon",
          params: { data: coupon }
        });
      }
    },

    async handleDelete(couponData) {
      const result = await this.showDeleteConfirmation();

      if (result.value) {
        await this.deleteCoupon(couponData.item);
      }
    },

    // Helper methods
    formatDate(date) {
      return date ? moment(date).format(DATE_FORMAT) : "";
    },

    getCouponById(id) {
      return this.couponCode.find(coupon => coupon._id === id);
    },

    showDeleteConfirmation() {
      return this.$swal({
        title: "Delete this coupon?",
        text: "Are you sure? You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Yes, Delete it!",
        cancelButtonColor: "#d33",
        reverseButtons: true
      });
    },

    async deleteCoupon(item) {
      try {
        await this.$store.dispatch("coupon/deleteCouponcode", {
          promoCode: item["Coupon Code"]
        });

        this.$toasted.success("Coupon Deleted Successfully");
        await this.getCouponCodeList();
      } catch (error) {
        console.error("Error deleting coupon:", error);
        this.$toasted.error("Failed to delete coupon");
      }
    }
  }
};
</script>

<style scoped>
.btn-edit-coupon {
    background: #fe7b08;
    border-color: #fe7b08;
}
</style>
